'use client'

import { useEffect, useMemo } from "react";
import { 
    email_login,
    email_signUp, 
    verifyOtp,
    resetPasswordForEmail,
    updatePassword ,
    get_access_token_supabase,
    logout_supabase,
    use_supabase, 
    linkIdentity, 
    unlinkIdentity, 
    islinkIdentity, 
    linkedIdentitys,
    login_user,
    third_party_login,
    resend_verification_supabase,
    modify_user_name_supabase
} from '../../utils/supabase-auth';

import { SsoSdk,SsoEvent, SsoError } from 'sso-frontend-sdk';
import { useRouter } from 'next/router';
import { getSsoInstance } from '../../utils/sso';
import { getOAuthCallbackUrl } from "../../utils/auth-redirect"

export default function useAuth() {
    // const [sdk, setSdk] = useState<SsoSdk | null>(null)
    const router = useRouter()

    const sdk = useMemo(() => {
        // if (typeof window === "undefined") return null; // SSR 阶段返回 null
        return getSsoInstance();
      }, []);

    useEffect(() => {
        if (sdk) {
            const loginUnsubscribe = sdk.on(SsoEvent.LOGIN, (user) => {
                console.log('用户已登录:', user);
                // router.replace('/')
            })

            const logoutUnsubscribe = sdk.on(SsoEvent.LOGOUT, () => {
                console.log('用户已登出:')
                // setIsAnonymous(false);
                // router.replace('/login')
            });

            const sessionExpiredUnsubscribe = sdk.on(SsoEvent.SESSION_EXPIRED, () => {
                console.log('会话已过期，请重新登录')
                sdk.clearTokens();
                // router.replace('/login')
            });

            const refresh_tokenUnsubscribe = sdk.on(SsoEvent.TOKEN_REFRESH,() => {
                console.log('token 刷新')
                // router.replace('/login')
            })

            const registerFailedUnsubscribe = sdk.on(SsoEvent.REGISTRATION_ERROR, (error) => {
                console.log('registerFailedUnsubscribe register failed', error)
            })

            return () => {
                loginUnsubscribe();
                logoutUnsubscribe();
                sessionExpiredUnsubscribe();
                refresh_tokenUnsubscribe();
                registerFailedUnsubscribe();
            }
        }
    }, [sdk])

    const login = async (email: string, password: string) => {
        try {
            if (sdk && !use_supabase) {
                return await sdk?.login({email, password})
            }
    
            let res = await email_login(email, password)
            if (!res?.session || !res?.user) {
                throw new Error('Please verify your email before signing in')
            }
            let result = {
                access_token: res.session.access_token,
                refresh_token: res.session.refresh_token,
                user: {
                    id: res.user.id,
                    email: res.user.email,
                    created_at: res.user.created_at,
                    updated_at: res.user.updated_at,
                    is_anonymous: res.user.is_anonymous,
                    email_confirmed_at: res.user.email_confirmed_at,
                    confirmation_sent_at: res.user.confirmation_sent_at,
                },
                expires_in:res.session.expires_in,
                token_type:res.session.token_type,
            }
            return result
        } catch (error) {
            if (error?.code == 401 || error?.status == 401) {
                router.replace('/login')
            }
            throw error
        }
    }

    const register = async (email: string, password: string, redirectTo = "") => {
        try {
            if (sdk && !use_supabase) {
                sdk.on(SsoEvent.REGISTRATION_ERROR, (error) => {
                    console.log('registerFailedUnsubscribe register failed sdk', error)
                })
                const options = redirectTo
                    ? { emailRedirectTo: redirectTo }
                    : undefined
                let res = await sdk?.register({email, password, options})
                console.log('register res sdk', res)
                return res
            } else {
                let res = await email_signUp(email, password)
                console.log('register res', res)
                const needsVerification = !res?.session || !(res.user?.email_confirmed_at || res.user?.confirmed_at)
                let result = {
                    success: true,
                    message:"",
                    requireVerification: needsVerification,
                    authTokens: res?.session ? {
                        access_token: res.session.access_token,
                        refresh_token: res.session.refresh_token,
                        expires_in: res.session.expires_in,
                        token_type: res.session.token_type,
                    } : null,
                    user: res?.user ? {
                        id: res.user.id,
                        email: res.user.email,
                        created_at: res.user.created_at,
                        updated_at: res.user.updated_at,
                        is_anonymous: res.user.is_anonymous,
                        email_confirmed_at: res.user.email_confirmed_at,
                        confirmation_sent_at: res.user.confirmation_sent_at,
                    } : null,
                }
                return result
            }
            return null
        } catch (error) {
            console.log('register error', error)
            if (error?.code == 401 || error?.status == 401) {
                router.replace('/login')
            }
            if (error instanceof SsoError) {
                console.log ('BigInt:', error.code);//400
                console.log ('Error message:', error.message ); // " registration failed "
                console.log ('Error type:', error.response.error_type ); // " other_error "
                console.log ('Details:', error.response.details ); // " User already registered... "
                return error.response
              }
            const message = error?.message || 'Registration failed'
            return { success: false, message, details: message }
        }
    }

    const verify_email = async (email: string, code: string, type: string, authType: string) => {
        try {
            if (sdk && !use_supabase) {
                return await sdk?.verifyEmail({email, token: code, reqType: type, authType: authType})
            }
            let res = await verifyOtp(email, code, type)
            return {
                success: true,
                message: "",
                ...res,
            }
        } catch (error) {
            if (error?.code == 401 || error?.status == 401) {
                router.replace('/login')
            }
            throw error
        }
    }

    const reset_password = async (email: string, redirectTo = "") => {
        try {
            if (sdk && !use_supabase) {
                const options = redirectTo
                    ? { emailRedirectTo: redirectTo }
                    : undefined
                return await sdk?.resetPassword({email, captcha_token: '', options})
            }
            return await resetPasswordForEmail(email, redirectTo || undefined)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    const update_password = async (password: string) => {
        try {
            if (sdk && !use_supabase) {
                return await sdk?.updatePassword({password})
            }
            return await updatePassword(password)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    const check_auth = async () => {
        try {
            if (sdk && !use_supabase) {
                let logined = await sdk?.isLoggedIn()
                if (!logined) {
                    let token = localStorage.getItem("sb-supa-auth-token")
                    if (token) {
                        logined = true
                    }
                }
                return logined
            }
            return await get_access_token()
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
        
    }

    const logout = async () => {
        try {
            if (sdk && !use_supabase) {
                clear_tokens()
                return await sdk?.logout()
            }
            return await logout_supabase()
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    const clear_tokens = async () => {
        if (sdk && !use_supabase) {
            localStorage.removeItem("sb-supa-auth-token")
            return await sdk?.clearTokens()
        }
        return null
    }

    const get_access_token = async () => {
        try {
            if (sdk && !use_supabase) {
                let tokens = await sdk?.getTokens()
                // console.log('get_access_token tokens', tokens)
                if (!tokens) {
                    let token = localStorage.getItem("sb-supa-auth-token")
                    if (token) {
                        tokens = JSON.parse(token)
                    }
                }
                return await tokens.access_token
            }
            return await get_access_token_supabase()
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    const link_identity = async (provider: string, redirectTo: string) => {
        try {
            // Telegram is a custom SSO flow, not a GoTrue OAuth provider.
            if (provider === "telegram") {
                const access_token = await get_access_token()
                if (!access_token) {
                    throw new Error("Please sign in again, then retry linking.")
                }
                const base_url = process.env.NEXT_PUBLIC_TELEGRAM_BASE_URL || window.location.origin
                const host = process.env.NEXT_PUBLIC_SSO_BASE_URL || ""
                const url = `${base_url}/telegram.html?data=${encodeURIComponent(access_token)}&redirectTo=${encodeURIComponent(redirectTo)}&host=${encodeURIComponent(host)}`
                window.location.href = url
                return
            }
            if (sdk && !use_supabase) {
                const res = await sdk?.linkIdentity({provider, options: {redirectTo: redirectTo}})
                if (res && res.url && res.url.length > 0) {
                    window.location.href = res.url
                    return res
                }
                throw new Error("Could not start social linking. Try again.")
            }
            return await linkIdentity(provider, redirectTo)  
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            throw error
        }
    }

    const unlink_identity = async (identity: any) => {
        console.log('unlink_identity identity', identity)
        try {
            if (sdk && !use_supabase) {
                if (identity.provider == 'telegram') {
                    return await sdk?.unlinkTelegramIdentity({
                        identity_id: identity.id || identity.identity_id,
                    })
                }
                return await sdk?.unlinkIdentity({identity_id: identity.identity_id})
            }
            if (identity?.provider == 'telegram') {
                const access = await get_access_token()
                const base = (process.env.NEXT_PUBLIC_SSO_BASE_URL || "").replace(/\/$/, "")
                if (!access || !base) throw new Error("Please sign in again, then retry.")
                const res = await fetch(`${base}/api/v1/auth/unlinkidentity/telegram`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${access}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ identity_id: identity.id || identity.identity_id }),
                })
                if (res.status === 401) throw { status: 401 }
                if (!res.ok) throw new Error(`Telegram unlink failed (${res.status})`)
                return await res.json().catch(() => true)
            }
            return await unlinkIdentity(identity.provider)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        } 
    }

    const asIdentityArray = (value: any): any[] => {
        if (Array.isArray(value)) return value
        if (Array.isArray(value?.data)) return value.data
        if (Array.isArray(value?.identities)) return value.identities
        return []
    }

    const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
        let timer: ReturnType<typeof setTimeout> | undefined
        try {
            return await Promise.race([
                promise,
                new Promise<T>((_, reject) => {
                    timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms)
                }),
            ])
        } finally {
            if (timer) clearTimeout(timer)
        }
    }

    const fetchTelegramIdentities = async () => {
        try {
            if (!use_supabase && typeof sdk?.getTelegramIdentities === "function") {
                const result = await withTimeout(sdk.getTelegramIdentities(), 8000, "getTelegramIdentities")
                const rows = asIdentityArray(result)
                if (rows.length > 0) return rows
            }
        } catch (error) {
            console.warn("getTelegramIdentities failed", error)
        }
        try {
            // get_access_token works in both SSO and Supabase mode; sdk.getTokens() is empty in Supabase mode.
            const access = await get_access_token()
            const base = (process.env.NEXT_PUBLIC_SSO_BASE_URL || "").replace(/\/$/, "")
            if (!access || !base) return []
            const res = await fetch(`${base}/api/v1/auth/telegram/identities`, {
                headers: { Authorization: `Bearer ${access}` },
            })
            const body = await res.json()
            return asIdentityArray(body)
        } catch (error) {
            console.warn("telegram identities fetch failed", error)
        }
        return []
    }

    const linked_identitys = async () => {
        try {
            if (sdk && !use_supabase) {
                const [identitiesResult, telegramResult] = await Promise.allSettled([
                    withTimeout(sdk.getLinkedIdentities(), 8000, "getLinkedIdentities"),
                    fetchTelegramIdentities(),
                ])
                const identities = asIdentityArray(
                    identitiesResult.status === "fulfilled" ? identitiesResult.value : [],
                )
                const telegram = asIdentityArray(
                    telegramResult.status === "fulfilled" ? telegramResult.value : [],
                ).map((item: any) => ({
                    ...item,
                    provider: item.provider || "telegram",
                    id: item.id || item.identity_id,
                    identity_id: item.identity_id || item.id,
                }))
                if (identitiesResult.status === "rejected") {
                    console.warn("getLinkedIdentities failed", identitiesResult.reason)
                }
                if (telegramResult.status === "rejected") {
                    console.warn("getTelegramIdentities failed", telegramResult.reason)
                }
                const merged = [...identities]
                telegram.forEach((item: any) => {
                    if (!merged.some((existing: any) => existing.provider === "telegram")) {
                        merged.push(item)
                    }
                })
                return merged
            }
            // Supabase Auth doesn't know about Telegram; those live in telegram.identities behind sso-backend.
            const [supabaseResult, telegramResult] = await Promise.allSettled([
                linkedIdentitys(),
                fetchTelegramIdentities(),
            ])
            if (supabaseResult.status === "rejected") throw supabaseResult.reason
            const supabaseIdentities: any = supabaseResult.value
            const base: any[] = Array.isArray(supabaseIdentities)
                ? supabaseIdentities
                : supabaseIdentities?.user?.identities || supabaseIdentities?.identities || []
            if (telegramResult.status === "rejected") {
                console.warn("getTelegramIdentities failed", telegramResult.reason)
            }
            const telegram = asIdentityArray(
                telegramResult.status === "fulfilled" ? telegramResult.value : [],
            ).map((item: any) => ({
                ...item,
                provider: item.provider || "telegram",
                id: item.id || item.identity_id,
                identity_id: item.identity_id || item.id,
            }))
            const merged = base.filter((item: any) => item.provider !== "telegram")
            if (telegram.length > 0) merged.push(telegram[0])
            return merged
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return []
        }
    }

    const is_link_identity = async (provider: string) => {
        try {
            if (sdk && !use_supabase) {
                const identities = await linked_identitys()
                if (Array.isArray(identities)) {
                    return identities.find((e: any) => e.provider === provider)
                }
                return null
            }
            if (provider === "telegram") {
                const identities = await linked_identitys()
                return Array.isArray(identities) ? identities.find((e: any) => e.provider === provider) || null : null
            }
            return await islinkIdentity(provider)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    const get_user = async () => {
        if (sdk && !use_supabase) {
            let res = await sdk?.getCurrentUser()
            if (!res) {
                let user = localStorage.getItem("sb-supa-auth-token")
                if (user) {
                    res = JSON.parse(user)
                    if (res && (res as any)?.user) {
                        res = (res as any)?.user as any
                    }
                }
            }
            console.log('get_user res', res)
            return res
        }
        return await login_user()
    }

    const modify_user = async (first_name: string,last_name: string) => {
        try {
            if (sdk && !use_supabase) {
                return await sdk?.updateProfile({first_name, last_name})
            }
            return await modify_user_name_supabase(first_name, last_name)
        } catch (error) {
            if (error?.code == 401 || error?.status == 401) {
                router.replace('/login')
            }
            throw error
        }
       
    }

    const login_with_identity = async (provider: string, redirectTo: string) => {
        try {
            if (sdk && !use_supabase) {
                let res = await sdk?.getProviderAuthorizeUrl({provider, options: {redirectTo}})
                if (res && res.url && res.url.length > 0) {
                    window.location.href = res.url
                }
                return res
            }
            return await third_party_login(provider, redirectTo)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
        
    }

    const login_with_google = async (provider: string, redirectTo: string) => {
        try {
            if (sdk && !use_supabase) {
                console.log('login_with_google sdk',sdk)
                let res = await sdk?.getOtherProviderAuthorizeUrl({
                    provider,
                    options: { redirectTo: redirectTo || getOAuthCallbackUrl() },
                })
                console.log('login_with_google res', res)
                if (res && res.url && res.url.length > 0) {
                    window.location.href = res.url
                }
                return res
            }
            return await third_party_login(provider, redirectTo || getOAuthCallbackUrl())
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
        
    }

    const resend_verification = async (email: string) => {
        try {
            if (sdk && !use_supabase) {
                return await sdk?.resendVerification({email_or_phone: email, is_phone: false})
            }
            return await resend_verification_supabase(email)
        } catch (error) {
            if (error.code == 401 || error.status == 401) {
                router.replace('/login')
            }
            return null
        }
    }

    return { 
        login,
         register, 
         verify_email, 
         reset_password, 
         update_password, 
         check_auth, 
         logout,
         get_access_token,
         link_identity, 
         unlink_identity,
         linked_identitys,
         is_link_identity, 
         get_user, 
         login_with_identity, 
         resend_verification,
         modify_user,
         login_with_google,
         clear_tokens
        }
}
